import React, { useState } from 'react';
import EditModalTemplate from '@component/gridgio/modal/dialog-modal/edit-modal-template';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import styled from 'styled-components';
import Gap from '@component/common/gap';
import LocationPicker from '@component/common/location-picker';
import type { ILocation } from '@util/kakao-map';
import IconTextBlock from '@component/common/block/icon-text-block';
import IconBlock from '@component/common/block/icon-block';
import Canvas from '@design-system/canvas';
import TextBlock from '@component/common/block/text-block';
import TextInput from '@component/common/input/text-input';
import TextAreaInput from '@component/common/input/text-area-input';
import { useModifyProjectMutation } from '@hook/gridgio/useApi';
import * as color from '@constant/color';
import * as size from '@constant/size';
import type { IProjectEditModalData } from '@datatype/gridgio/type';
import { ETheme } from '@datatype/type';

const Wrapper = styled.div`
  display: flex;
  width: ${size.GRIDGIO_LARGE_MODAL_WIDTH};
  flex-direction: column;
`;

const LocationContainer = styled.div`
  position: relative;
  width: 100%;
`;

const LocationClearButtonContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${color.GRAY_3};
  cursor: pointer;
`;

const LABEL_DESIGN = {
  box: { width: '100%', alignX: 'left' },
  text: { size: 'medium', color: color.BLACK_2, bold: true },
} as const;

const INPUT_DESIGN = {
  box: {
    width: '100%',
    height: '42px',
    padding: `0 ${size.MARGIN_1}`,
    border: true,
    borderWidth: size.BORDER_WIDTH_1,
    borderColor: color.GRAY_2,
    borderRadius: size.BORDER_RADIUS_1,
    alignX: 'left',
  },
  text: { size: 'medium', color: color.BLACK_2 },
} as const;

interface IProps {
  data: IProjectEditModalData['data'];
}

const ProjectEditModal: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const [name, setName] = useState(data.name);
  const [description, setDescription] = useState(data.description ?? '');
  const initialLocation: ILocation | null =
    data.coordinate?.latitude != null && data.coordinate?.longitude != null
      ? {
          name: data.place ?? '',
          address: data.address ?? '',
          latitude: data.coordinate.latitude,
          longitude: data.coordinate.longitude,
        }
      : null;
  const [location, setLocation] = useState<ILocation | null>(initialLocation);
  const [isLocationChanged, setIsLocationChanged] = useState(false);
  const isChanged = name.trim() !== data.name || description.trim() !== (data.description ?? '') || isLocationChanged;
  const [isLocationSelecting, setIsLocationSelecting] = useState(false);
  const mutation = useModifyProjectMutation();

  const handleClose = () => {
    if (!mutation.isPending) dispatch(modalActions.hideModal());
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || !isChanged || mutation.isPending) return;

    try {
      await mutation.mutateAsync({
        projectKey: data.key,
        name: trimmedName,
        description: description.trim(),
        place: isLocationChanged ? (location?.name ?? '') : data.place,
        address: isLocationChanged ? (location?.address ?? '') : data.address,
        coordinate: isLocationChanged
          ? location
            ? { latitude: location.latitude, longitude: location.longitude }
            : {}
          : data.coordinate,
      });
      dispatch(modalActions.hideModal());
    } catch {}
  };

  if (isLocationSelecting) {
    return (
      <Wrapper>
        <LocationPicker
          value={location}
          handleConfirm={(selected) => {
            setLocation(selected);
            setIsLocationChanged(true);
            setIsLocationSelecting(false);
          }}
          handleCancel={() => setIsLocationSelecting(false)}
        />
      </Wrapper>
    );
  }

  return (
    <EditModalTemplate
      title='프로젝트 편집'
      width={size.GRIDGIO_LARGE_MODAL_WIDTH}
      isPending={mutation.isPending}
      saveDisabled={!name.trim() || !isChanged}
      handleCancel={handleClose}
      handleSave={handleSave}
      input={
        <>
          <TextBlock text='프로젝트 이름' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextInput
            name='project-name'
            value={name}
            placeholder='프로젝트 이름을 입력해주세요.'
            design={INPUT_DESIGN}
            onChange={(event) => {
              if (!mutation.isPending) setName(event.target.value);
            }}
          />
          <Gap height={size.MARGIN_2} />
          <TextBlock text='프로젝트 설명 (선택)' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextAreaInput
            name='project-description'
            value={description}
            placeholder='프로젝트 설명을 입력해주세요.'
            row={3}
            max={500}
            design={{
              ...INPUT_DESIGN,
              box: { ...INPUT_DESIGN.box, height: '80px', padding: size.MARGIN_1 },
            }}
            onChange={(event) => {
              if (!mutation.isPending) setDescription(event.target.value);
            }}
          />
          <Gap height={size.MARGIN_2} />
          <TextBlock text='프로젝트 위치 (선택)' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <LocationContainer>
            <Canvas
              theme={ETheme.WHITE}
              width='100%'
              padding={location ? `${size.MARGIN_1} 40px ${size.MARGIN_1} ${size.MARGIN_1}` : size.MARGIN_1}
              border
              handleClick={() => {
                if (!mutation.isPending) setIsLocationSelecting(true);
              }}>
              <IconTextBlock
                icon={location ? 'map-pin' : 'map-pin-plus'}
                text={location ? location.name || '선택한 위치' : '현장 위치 찾기'}
                design={{
                  box: { width: '100%', alignX: 'left', gap: '5px' },
                  text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
                  icon: { size: 'medium', color: color.BLACK_2 },
                }}
              />
              <Gap height='5px' />
              <TextBlock
                text={
                  location
                    ? location.address || '지도에서 지정한 위치'
                    : '현장 주소를 검색하거나 지도에서 위치를 선택하세요.'
                }
                design={{ box: { width: '100%', alignX: 'left' }, text: { size: 'small', color: color.GRAY_1 } }}
              />
            </Canvas>
            {location && (
              <LocationClearButtonContainer
                onClick={() => {
                  if (mutation.isPending) return;
                  setLocation(null);
                  setIsLocationChanged(true);
                }}>
                <IconBlock icon='close' design={{ icon: { size: 'tiny', color: color.BLACK_2 } }} />
              </LocationClearButtonContainer>
            )}
          </LocationContainer>
        </>
      }
    />
  );
};

export default ProjectEditModal;
